import React, { useRef, useState } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));

export default function AiRecommendationOverlay({
  pageIndex,
  recommendations,
  onChange,
  onDelete,
  disabled = false,
}) {
  const dragRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const pageRecommendations = recommendations.filter((item) => Number(item.page || 1) === pageIndex + 1);

  if (pageRecommendations.length === 0) return null;

  const beginDrag = (event, item) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const pageEl = event.currentTarget.closest('[data-ai-page-overlay]');
    const rect = pageEl?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      id: item.id,
      rect,
      offsetX: event.clientX - rect.left - item.x * rect.width,
      offsetY: event.clientY - rect.top - item.y * rect.height,
    };
    setDraggingId(item.id);
    window.addEventListener('pointermove', handleDragMove);
    window.addEventListener('pointerup', endDrag);
  };

  const handleDragMove = (event) => {
    const state = dragRef.current;
    if (!state) return;
    const nextX = (event.clientX - state.rect.left - state.offsetX) / state.rect.width;
    const nextY = (event.clientY - state.rect.top - state.offsetY) / state.rect.height;
    onChange(state.id, {
      x: clamp(nextX, 0.02, 0.92),
      y: clamp(nextY, 0.02, 0.92),
    });
  };

  const endDrag = () => {
    dragRef.current = null;
    setDraggingId(null);
    window.removeEventListener('pointermove', handleDragMove);
    window.removeEventListener('pointerup', endDrag);
  };

  return (
    <Box
      data-ai-page-overlay
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {pageRecommendations.map((item) => (
        <Box
          key={item.id}
          sx={{
            position: 'absolute',
            left: `${clamp(item.x) * 100}%`,
            top: `${clamp(item.y) * 100}%`,
            width: `${clamp(item.width || 0.24, 0.12, 0.4) * 100}%`,
            minHeight: `${clamp(item.height || 0.08, 0.04, 0.24) * 100}%`,
            border: '1px dashed #f97316',
            bgcolor: 'rgba(255, 247, 237, 0.94)',
            borderRadius: 1,
            boxShadow: draggingId === item.id ? 4 : 2,
            p: 0.75,
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Tooltip title="Drag AI suggestion">
              <IconButton size="small" onPointerDown={(e) => beginDrag(e, item)}>
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#9a3412', flex: 1 }}>
              AI suggestion
            </Typography>
            <Tooltip title="Delete suggestion">
              <IconButton size="small" onClick={() => onDelete(item.id)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            multiline
            minRows={2}
            fullWidth
            size="small"
            value={item.text || ''}
            onChange={(event) => onChange(item.id, { text: event.target.value })}
            inputProps={{ 'aria-label': 'AI worksheet suggestion text' }}
            sx={{
              '& textarea': {
                fontSize: '0.78rem',
                lineHeight: 1.35,
              },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
              },
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

